import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { RegistrationDataHolderService } from '@zero/onboarding/domain';
import { OnboardingData } from '@zero/shared/util-models';
import { commonRoutes } from 'constants/web-app-constants';
import { environment } from 'environments/environment';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { PromotionService } from '../promotion/promotion.service';
import { UserMessagesHolderService } from '../user-messages/user-messages-holder.service';

@Injectable({
  providedIn: 'root',
})
export class RegistrationUserDataService {
  public ongoingRequest = false;
  public initialUserDataLoad: Promise<void>;
  public resolveInitialUserDataLoad: () => void;

  readonly #http = inject(HttpClient);
  readonly #router = inject(Router);
  readonly #dataHolderService = inject(RegistrationDataHolderService);
  readonly #msgService = inject(UserMessagesHolderService);
  readonly #promoService = inject(PromotionService);
  #previousUrl = '';

  public get previousUrl(): string {
    return this.#previousUrl.split('/').pop();
  }

  constructor() {
    this.initialUserDataLoad = new Promise<void>((resolve) => (this.resolveInitialUserDataLoad = resolve));
    let currentUrl = this.#router.url;
    this.#router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.#previousUrl = currentUrl;
        currentUrl = event.url;
      }
    });
  }

  public saveUserData(data: any, finishDataEntry?: boolean, sendContractDocumentsByMail?: boolean): Observable<any> {
    let httpParams = new HttpParams();
    if (finishDataEntry === true) {
      httpParams = httpParams.append('finishEntry', 'true');
    }
    if (sendContractDocumentsByMail === true) {
      httpParams = httpParams.append('sendContractDocumentsByMail', 'true');
    }

    return this.#http
      .post<any>(`${environment.apihost}/api/user/data`, data, {
        params: httpParams,
      })
      .pipe(map((result) => this.fixBirthdate(result)));
  }

  public loadUserData(): Promise<any> {
    this.ongoingRequest = true;
    return new Promise<any>((resolve, reject) => {
      firstValueFrom(this.#http.get(`${environment.apihost}/api/user/data`))
        .then((result) => this.fixBirthdate(result))
        .then(
          (res) => {
            // Success
            this.ongoingRequest = false;
            if (!res['nationality']) {
              res['nationality'] = 'DE';
            }
            if (!res['geburtsland']) {
              res['geburtsland'] = 'DE';
            }
            if (!res['land']) {
              res['land'] = 'DE';
            }

            if (res['abruftin'] === null || res['abruftin'] === 'undefined') {
              res['abruftin'] = true;
            }

            resolve(res);
            this.resolveInitialUserDataLoad();
          },
          (msg) => {
            // Error
            this.ongoingRequest = false;
            reject(msg);
          },
        )
        .catch(() => {
          this.ongoingRequest = false;
        });
    });
  }

  public goNextStep(fromStep: string, route: ActivatedRoute, finishDataEntry?: boolean, sendContractDocumentsByMail?: boolean): void {
    this.#msgService.clearAllMessages();
    this.ongoingRequest = true;
    const promoCode = this.#dataHolderService.personData['promoCode'] ?? this.#promoService.getAccountOpenReferenceId();
    this.#dataHolderService.personData['promoCode'] = promoCode;

    this.saveUserData(this.#dataHolderService.personData, finishDataEntry, sendContractDocumentsByMail).subscribe({
      next: (result) => {
        this.ongoingRequest = false;
        this.#dataHolderService.personData = result;

        let destination = '';
        switch (fromStep) {
          case 'person':
            destination = '../kontaktdaten';
            break;
          case 'kontaktdaten':
            destination = '../mobilnrverifikation';
            break;
          case 'mobilnrverifikation':
            destination = '../konto';
            break;
          case 'konto':
            destination = '../ersteinzahlung';
            break;
          case 'ersteinzahlung':
            destination = '../steuern';
            break;
          case 'steuern':
            destination = '../kenntnisse';
            break;
          case 'kenntnisse':
            destination = '../krypto';
            break;
          case 'krypto':
            destination = '../zuwendungen';
            break;
          case 'provision':
            destination = '../infos';
            break;
          case 'infos':
            if (result.status === 'IN_IDENTIFICATION' && result.identificationStartOptions) {
              destination = '../../ident';
            } else {
              this.#msgService.addErrorMessage('Es ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.');
              return;
            }
            break;
          default:
            break;
        }

        if (promoCode?.length) {
          this.#promoService.deletePromotionInfo();
        }

        this.#router.navigate([destination], {
          relativeTo: route,
        });
      },
      error: (error) => {
        this.ongoingRequest = false;
        const msg = error.error?.message ? error.error.message : error.message;
        this.#msgService.addErrorMessage(msg);
      },
    });
  }

  public back(fromStep: string, route: ActivatedRoute): void {
    let destination = '';
    switch (fromStep) {
      case 'person':
        destination = '../../';
        break;
      case 'kontaktdaten':
        destination = '../person';
        break;
      case 'mobilnrverifikation':
        destination = '../kontaktdaten';
        break;
      case 'konto':
        destination = '../mobilnrverifikation';
        break;
      case 'ersteinzahlung':
        destination = '../konto';
        break;
      case 'steuern':
        destination = '../ersteinzahlung';
        break;
      case 'kenntnisse':
        destination = '../steuern';
        break;
      case 'krypto':
        destination = '../kenntnisse';
        break;
      case 'provision':
        destination = '../krypto';
        break;
      case 'infos':
        destination = '../zuwendungen';
        break;
      default:
        break;
    }

    this.#router.navigate([destination], {
      relativeTo: route,
    });
  }

  public abbrechen(): void {
    this.#dataHolderService.personData = {} as OnboardingData;
    this.#router.navigate([commonRoutes.postlogin.member_home]);
  }

  public getRegistrationPdfUrl(): string {
    return environment.apihost + environment.pregenerateRegistrationPdfUrl;
  }

  public isMobileNrValid(): Promise<boolean> {
    return firstValueFrom(
      this.#http
        .get<{ mobilenrVerified: boolean }>(environment.apihost + environment.mobilenrCheckUrl)
        .pipe(map((res) => res.mobilenrVerified)),
    );
  }

  public loadIban(): Promise<string> {
    return firstValueFrom(
      this.#http.get(environment.apihost + environment.postIdentificationIbanDisplayUrl, {
        responseType: 'text',
      }),
    );
  }

  public fixBirthdate(personData: any): any {
    if (personData?.geburtsdatum) {
      // cutting off the time information since at least in safari the ngbDatePicker cannot handle the zone offset provided
      const fixed = personData.geburtsdatum.split('T')[0];

      personData.geburtsdatum = fixed;
    }
    return personData;
  }
}
